import { scaleLinear } from 'd3';
import { BarProps, getColoursProps } from 'wind-bars.types';

const getColours = ({ warningThreshold, alertThreshold, value }: getColoursProps) => {
    if (alertThreshold !== undefined && value > alertThreshold) {
        return { top: '#FA6161', bottom: '#E93F3F' };
    }

    if (warningThreshold !== undefined && value > warningThreshold) {
        return { top: '#FF981B', bottom: '#CE7100' };
    }

    return { top: '#74A30F', bottom: '#577E00' };
};

const BASIC_SVG_PADDING = 4;
const ARROW_WIDTH = 34;
const PADDING_INLINE = 24; // 12 * 2
const NUMBER_WIDTH = 40; // 28 for the numbers, 12 for the additional padding
const PADDING_BLOCK = 32; // 16 * 2

export const WindBar = ({
    min,
    max,
    minLul10min,
    maxGust10min,
    windSpeedAverage10m,
    windSpeedAverage1m,
    warningThreshold,
    alertThreshold,
    step,
    width,
    height,
    arrowTextColor,
    color,
}: BarProps) => {
    const LINEAR_GRADIENT_ID = Math.random();

    const svgHeight = height - PADDING_BLOCK;
    const svgWidth = Math.min(width - ARROW_WIDTH - PADDING_INLINE - NUMBER_WIDTH, 250); // max width of bar is 250px
    const diffWidth = width - ARROW_WIDTH - PADDING_INLINE - NUMBER_WIDTH - svgWidth;

    const minLulPosition = scaleLinear().domain([min, max]).range([0, svgHeight])(Math.max(minLul10min, 0));
    const maxGustPosition = scaleLinear().domain([min, max]).range([0, svgHeight])(Math.min(maxGust10min, max));
    const windSpeedAverage10mPosition = scaleLinear().domain([min, max]).range([0, svgHeight])(windSpeedAverage10m);
    const windSpeedAverage1mPosition = scaleLinear().domain([min, max]).range([0, svgHeight])(Math.min(windSpeedAverage1m, max));
    const colour = getColours({ warningThreshold, alertThreshold, value: maxGust10min });

    return (
        <svg height={height} width={width}>
            <g transform={`translate(${PADDING_INLINE / 2 + diffWidth / 2}, ${PADDING_BLOCK / 2})`}>
                <linearGradient id={`gradient-${LINEAR_GRADIENT_ID}`} x1='0%' y1='0%' x2='0%' y2='100%'>
                    <stop offset='0%' stopColor={colour.top} />
                    <stop offset='70%' stopColor={colour.bottom} />
                </linearGradient>
                {windSpeedAverage1mPosition && windSpeedAverage1m && (
                    <g transform={`translate(0, ${svgHeight - windSpeedAverage1mPosition - 10})`}>
                        <polygon points='0,0 24,0 34,10 24,22 0,22' fill={color} style={{ strokeLinejoin: 'round', stroke: color, strokeWidth: 2 }} />
                        <text
                            transform={`translate(6, 12)`}
                            stroke={arrowTextColor}
                            dominantBaseline={'middle'}
                            textAnchor={'start'}
                            fontSize={14}
                            fill={arrowTextColor}
                        >
                            {windSpeedAverage1m}
                        </text>
                    </g>
                )}
                <g transform={`translate(${ARROW_WIDTH + 2}, 0)`}>
                    {minLulPosition !== undefined && maxGustPosition !== undefined && (
                        <g transform={`translate(2, ${svgHeight - minLulPosition - (maxGustPosition - minLulPosition)})`}>
                            <rect
                                width={svgWidth}
                                height={maxGustPosition - minLulPosition + BASIC_SVG_PADDING / 2}
                                fill={`url(#gradient-${LINEAR_GRADIENT_ID}`}
                                rx={4}
                            />
                        </g>
                    )}

                    {windSpeedAverage10mPosition !== undefined && (
                        <g transform={`translate(0, ${svgHeight - windSpeedAverage10mPosition - 1})`}>
                            <rect width={svgWidth + BASIC_SVG_PADDING} height={2} fill={'#333'} />
                        </g>
                    )}
                    <g transform={`translate(0, -2)`}>
                        <rect width={svgWidth + BASIC_SVG_PADDING} height={svgHeight + BASIC_SVG_PADDING} stroke={color} strokeWidth={1} rx={2} fill={'none'} />
                    </g>
                    {Array.from({ length: (max - min) / (step / 2) + 1 }, (_, i) => min + (i * step) / 2).map((num) => {
                        const yPosition = scaleLinear().domain([max, min]).range([0, svgHeight])(num);
                        if (num % step === 0) {
                            return (
                                <g key={`${num}`} transform={`translate(0, ${yPosition})`}>
                                    <text
                                        transform={`translate(${svgWidth + BASIC_SVG_PADDING + 12}, 0)`}
                                        dominantBaseline='central'
                                        fill={color}
                                        fontSize={14}
                                    >
                                        {num === max ? `${max}+` : num}
                                    </text>
                                    {![min, max].includes(num) && <line x1={0} x2={svgWidth + BASIC_SVG_PADDING + 10} stroke={color} opacity={0.2} />}
                                </g>
                            );
                        }
                        return (
                            <g key={`${num}`} transform={`translate(0, ${yPosition})`}>
                                <line x1={svgWidth} x2={svgWidth + 6} stroke={color} opacity={0.2} />
                            </g>
                        );
                    })}
                </g>
            </g>
        </svg>
    );
};
